import { SessionStorageKey, sessionStore } from 'utils/typedStorage';
export const ROUTE_AFTER_REDIRECT_KEY = 'routeAfterRedirect';

const BULLHORN_EMBED_ROUTES = [
  '/bullhorn/job-tab',
  '/bullhorn/candidate-tab',
] as const;

const LOGIN_ROUTES_WITHOUT_EMBED_ZOOM = ['/login', '/authenticate'] as const;

const BULLHORN_ANCESTOR_HOSTS = [
  'bullhorn.com',
  'bullhornstaffing.com',
] as const;

export function isBullhornEmbedRoute(pathname: string): boolean {
  return BULLHORN_EMBED_ROUTES.some((route) => pathname.startsWith(route));
}

export function isInIframe(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    // swallow-ok: Blocked parent-window inspection conservatively identifies a frame; Bullhorn embed mode still requires an allowed ancestor or the recorded handshake.
    return true;
  }
}

function ancestorOrigins(): string[] {
  const origins = window.location.ancestorOrigins;
  if (!origins) {
    return [];
  }
  return Array.from(origins);
}

function hostMatchesBullhorn(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return BULLHORN_ANCESTOR_HOSTS.some(
    (allowed) => host === allowed || host.endsWith(`.${allowed}`)
  );
}

export function isBullhornAncestorOrigin(origin: string): boolean {
  try {
    return hostMatchesBullhorn(new URL(origin).hostname);
  } catch {
    // swallow-ok: Malformed ancestor URLs never match the Bullhorn allowlist; isEmbeddedInBullhorn rejects untrusted origins.
    return false;
  }
}

export function isEmbeddedInBullhorn(): boolean {
  if (!isInIframe()) {
    return false;
  }

  const origins = ancestorOrigins();
  if (origins.length > 0) {
    return origins.some(isBullhornAncestorOrigin);
  }

  // Firefox has no ancestorOrigins. Stay in embed mode only if this frame
  // already landed on a Bullhorn handshake route.
  return sessionStore.get(SessionStorageKey.BullhornSidebarHidden) === 'true';
}

export function isBullhornEmbedSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return isEmbeddedInBullhorn();
}

export function shouldApplyBullhornEmbedZoom(pathname: string): boolean {
  if (!isBullhornEmbedSession()) {
    return false;
  }

  return !LOGIN_ROUTES_WITHOUT_EMBED_ZOOM.some((route) =>
    pathname.startsWith(route)
  );
}

export function markBullhornEmbedSession(): void {
  if (!isInIframe()) {
    return;
  }
  sessionStore.set(SessionStorageKey.BullhornSidebarHidden, 'true');
}

export function clearTopLevelBullhornEmbedSession(): void {
  if (typeof window === 'undefined' || isInIframe()) {
    return;
  }
  sessionStore.remove(SessionStorageKey.BullhornSidebarHidden);
}
